import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';

type SortDirection = 'asc' | 'desc';
type SortField = string;

const DEFAULT_COLUMN_ORDER: SortField[] = [
  'transactionReference', 'projectNumber', 'companyName', 'customerName',
  'paymentMethodLabel', 'projectValueSek', 'customerFeeSek', 'companyFeeSek',
  'transactionFeeSek', 'projectFeeSek', 'totalPaidSek',
  'etappioRevenueSek', 'etappioMarginSek', 'etappioMarginPct',
  'status', 'createdAt'
];

const CURRENCY_FIELDS = [
  'projectValueSek', 'customerFeeSek', 'companyFeeSek', 'transactionFeeSek',
  'projectFeeSek', 'totalPaidSek', 'etappioRevenueSek', 'etappioCostSek', 'etappioMarginSek'
];

const columnLabelKeys: Record<string, string> = {
  transactionReference: 'adminTransactionList.columns.transactionReference',
  projectNumber: 'adminTransactionList.columns.projectNumber',
  companyName: 'adminTransactionList.columns.companyName',
  companyNumber: 'adminTransactionList.columns.companyNumber',
  customerName: 'adminTransactionList.columns.customerName',
  customerNumber: 'adminTransactionList.columns.customerNumber',
  projectDescription: 'adminTransactionList.columns.projectDescription',
  paymentMethod: 'adminTransactionList.columns.paymentMethod',
  paymentMethodLabel: 'adminTransactionList.columns.paymentMethod',
  projectValueSek: 'adminTransactionList.columns.projectValueSek',
  customerFeeSek: 'adminTransactionList.columns.customerFeeSek',
  companyFeeSek: 'adminTransactionList.columns.companyFeeSek',
  transactionFeeSek: 'adminTransactionList.columns.transactionFeeSek',
  projectFeeSek: 'adminTransactionList.columns.projectFeeSek',
  totalPaidSek: 'adminTransactionList.columns.totalPaidSek',
  etappioRevenueSek: 'adminTransactionList.columns.etappioRevenueSek',
  etappioCostSek: 'adminTransactionList.columns.etappioCostSek',
  etappioMarginSek: 'adminTransactionList.columns.etappioMarginSek',
  etappioMarginPct: 'adminTransactionList.columns.etappioMarginPct',
  configName: 'adminTransactionList.columns.configName',
  configType: 'adminTransactionList.columns.configType',
  feePct: 'adminTransactionList.columns.feePct',
  paidByName: 'adminTransactionList.columns.paidByName',
  status: 'adminTransactionList.columns.status',
  createdAt: 'adminTransactionList.columns.createdAt'
};

interface ColumnFilter {
  [key: string]: string;
}

const currencyFormatter = new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const pctFormatter = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch {
    return '';
  }
}

function getStatusMeta(status: string): { className: string; labelKey: string | null } {
  switch (status) {
    case 'completed':
    case 'held':
      return { className: 'bg-success', labelKey: 'adminTransactionList.status.completed' };
    case 'pending':
      return { className: 'bg-warning text-dark', labelKey: 'adminTransactionList.status.pending' };
    case 'released':
      return { className: 'bg-success', labelKey: 'adminTransactionList.status.released' };
    case 'releasing':
      return { className: 'bg-info', labelKey: 'adminTransactionList.status.releasing' };
    case 'failed':
      return { className: 'bg-danger', labelKey: 'adminTransactionList.status.failed' };
    case 'cancelled':
      return { className: 'bg-secondary', labelKey: 'adminTransactionList.status.cancelled' };
    case 'partially_refunded':
      return { className: 'bg-warning text-dark', labelKey: 'adminTransactionList.status.partiallyRefunded' };
    case 'refunded':
      return { className: 'bg-secondary', labelKey: 'adminTransactionList.status.refunded' };
    case 'disputed':
      return { className: 'bg-danger', labelKey: 'adminTransactionList.status.disputed' };
    default:
      return { className: 'bg-secondary', labelKey: null };
  }
}

export function AdminTransactionList() {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filters, setFilters] = useState<ColumnFilter>({});

  const [columnOrder, setColumnOrder] = useState<SortField[]>(DEFAULT_COLUMN_ORDER);
  const [draggedColumn, setDraggedColumn] = useState<SortField | null>(null);

  const columnLabel = (field: string) => (columnLabelKeys[field] ? t(columnLabelKeys[field]) : field);
  const statusLabel = (status: string) => {
    const meta = getStatusMeta(status);
    return meta.labelKey ? t(meta.labelKey) : status;
  };

  useEffect(() => {
    loadData();
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const prefs = await adminApi.getPreferences();
      if (prefs.adminTransactionListColumns && Array.isArray(prefs.adminTransactionListColumns)) {
        const validColumns = prefs.adminTransactionListColumns.filter(
          (col: string) => DEFAULT_COLUMN_ORDER.includes(col)
        );
        const newColumns = DEFAULT_COLUMN_ORDER.filter(col => !validColumns.includes(col));
        setColumnOrder([...validColumns, ...newColumns]);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const saveColumnOrder = useCallback(async (newOrder: SortField[]) => {
    try {
      await adminApi.savePreference('adminTransactionListColumns', newOrder);
    } catch (error) {
      console.error('Failed to save column order:', error);
    }
  }, []);

  const loadData = async () => {
    try {
      const data = await adminApi.getTransactions();
      setTransactions(data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...transactions];

    Object.entries(filters).forEach(([field, value]) => {
      if (value) {
        result = result.filter(tx => {
          const fieldValue = tx[field];
          return String(fieldValue || '').toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    result.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    return result;
  }, [transactions, filters, sortField, sortDirection]);

  // Summary calculations
  const summary = useMemo(() => {
    const totalProjectValue = transactions.reduce((s, t) => s + (t.projectValueSek || 0), 0);
    const totalCustomerFee = transactions.reduce((s, t) => s + (t.customerFeeSek || 0), 0);
    const totalCompanyFee = transactions.reduce((s, t) => s + (t.companyFeeSek || 0), 0);
    const totalMargin = transactions.reduce((s, t) => s + (t.etappioMarginSek || 0), 0);
    const totalRevenue = transactions.reduce((s, t) => s + (t.etappioRevenueSek || 0), 0);
    const marginPct = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;
    return { totalProjectValue, totalCustomerFee, totalCompanyFee, totalMargin, marginPct };
  }, [transactions]);

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <i className="feather-chevrons-up-down ms-1 opacity-25"></i>;
    }
    return sortDirection === 'asc' ? (
      <i className="feather-chevron-up ms-1"></i>
    ) : (
      <i className="feather-chevron-down ms-1"></i>
    );
  };

  const formatCellValue = (field: string, value: any): React.ReactNode => {
    if (field === 'status') {
      const meta = getStatusMeta(value);
      return <span className={`badge ${meta.className}`}>{meta.labelKey ? t(meta.labelKey) : value}</span>;
    }
    if (field === 'createdAt') return formatDateTime(value);
    if (CURRENCY_FIELDS.includes(field)) return currencyFormatter.format(value || 0);
    if (field === 'etappioMarginPct' || field === 'feePct') return `${pctFormatter.format(value || 0)}%`;
    return value ?? '';
  };

  // Drag and drop
  const handleDragStart = (e: React.DragEvent, field: SortField) => {
    setDraggedColumn(field);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetField: SortField) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetField) return;
    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetField);
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);
    setColumnOrder(newOrder);
    saveColumnOrder(newOrder);
    setDraggedColumn(null);
  };
  const handleDragEnd = () => setDraggedColumn(null);

  const exportToExcel = () => {
    const headers = columnOrder.map(field => columnLabel(field));
    const rows = filteredAndSorted.map(tx =>
      columnOrder.map(field => {
        if (field === 'createdAt') return formatDateTime(tx[field]);
        if (field === 'status') return statusLabel(tx[field]);
        if (CURRENCY_FIELDS.includes(field)) return tx[field] || 0;
        if (field === 'etappioMarginPct' || field === 'feePct') return tx[field] || 0;
        return tx[field] ?? '';
      })
    );
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\n');
    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `admin_transactions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminTransactionList.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('admin.nav.transactions') },
            { label: t('admin.nav.transactionList') }
          ]} />
        </div>
      </div>

      {/* Summary cards */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <Card>
            <div className="text-center">
              <div className="text-muted small mb-1">{t('adminTransactionList.summary.totalProjectValue')}</div>
              <h5 className="fw-bold text-primary mb-0">{currencyFormatter.format(summary.totalProjectValue)}</h5>
            </div>
          </Card>
        </div>
        <div className="col-sm-6 col-xl-3">
          <Card>
            <div className="text-center">
              <div className="text-muted small mb-1">{t('adminTransactionList.summary.totalCustomerFees')}</div>
              <h5 className="fw-bold text-info mb-0">{currencyFormatter.format(summary.totalCustomerFee)}</h5>
            </div>
          </Card>
        </div>
        <div className="col-sm-6 col-xl-3">
          <Card>
            <div className="text-center">
              <div className="text-muted small mb-1">{t('adminTransactionList.summary.totalCompanyFees')}</div>
              <h5 className="fw-bold text-warning mb-0">{currencyFormatter.format(summary.totalCompanyFee)}</h5>
            </div>
          </Card>
        </div>
        <div className="col-sm-6 col-xl-3">
          <Card>
            <div className="text-center">
              <div className="text-muted small mb-1">{t('adminTransactionList.summary.netMargin')}</div>
              <h5 className="fw-bold text-success mb-0">
                {currencyFormatter.format(summary.totalMargin)}
                <span className="fs-12 fw-normal ms-1">({pctFormatter.format(summary.marginPct)}%)</span>
              </h5>
            </div>
          </Card>
        </div>
      </div>

      <Card>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="text-muted small">{t('adminTransactionList.count', { count: filteredAndSorted.length })}</div>
          <button className="btn btn-light-brand btn-sm" onClick={exportToExcel}>
            <i className="feather-download me-1"></i>
            <span className="d-none d-sm-inline">{t('adminTransactionList.export')}</span>
          </button>
        </div>

        {/* Desktop table */}
        <div className="d-none d-md-block">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  {columnOrder.map(field => (
                    <th
                      key={field}
                      draggable
                      onDragStart={e => handleDragStart(e, field)}
                      onDragOver={handleDragOver}
                      onDrop={e => handleDrop(e, field)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleSort(field)}
                      style={{
                        cursor: 'grab',
                        whiteSpace: 'nowrap',
                        backgroundColor: draggedColumn === field ? '#e3f2fd' : undefined,
                        transition: 'background-color 0.2s'
                      }}
                      title={t('adminTransactionList.dragToReorder')}
                    >
                      <i className="feather-move me-1" style={{ opacity: 0.4, fontSize: '12px' }}></i>
                      {columnLabel(field)}
                      {renderSortIcon(field)}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columnOrder.map(field => (
                    <th key={`filter-${field}`} className="p-1">
                      {field === 'status' ? (
                        <select
                          className="form-select form-select-sm"
                          value={filters[field] || ''}
                          onChange={e => handleFilterChange(field, e.target.value)}
                        >
                          <option value="">{t('adminTransactionList.filterAll')}</option>
                          <option value="completed">{t('adminTransactionList.status.completed')}</option>
                          <option value="pending">{t('adminTransactionList.status.pending')}</option>
                          <option value="failed">{t('adminTransactionList.status.failed')}</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={t('adminTransactionList.filterPlaceholder')}
                          value={filters[field] || ''}
                          onChange={e => handleFilterChange(field, e.target.value)}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.length === 0 ? (
                  <tr>
                    <td colSpan={columnOrder.length} className="text-center text-muted py-4">
                      {transactions.length === 0 ? t('adminTransactionList.empty') : t('adminTransactionList.noResults')}
                    </td>
                  </tr>
                ) : (
                  filteredAndSorted.map(tx => (
                    <tr
                      key={tx.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => { window.location.href = `/admin/transactions/${tx.id}`; }}
                    >
                      {columnOrder.map(field => (
                        <td key={field} style={{ whiteSpace: CURRENCY_FIELDS.includes(field) ? 'nowrap' : undefined }}>
                          {formatCellValue(field, tx[field])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile card view */}
        <div className="d-md-none">
          <div className="mb-3">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t('adminTransactionList.searchPlaceholder')}
              value={filters['transactionReference'] || ''}
              onChange={e => handleFilterChange('transactionReference', e.target.value)}
            />
          </div>
          {filteredAndSorted.length === 0 ? (
            <p className="text-muted text-center py-3">
              {transactions.length === 0 ? t('adminTransactionList.empty') : t('adminTransactionList.noResults')}
            </p>
          ) : (
            filteredAndSorted.map(tx => {
              const meta = getStatusMeta(tx.status);
              return (
                <div key={tx.id} className="card mb-2">
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <span className="fw-semibold small text-truncate">{tx.transactionReference}</span>
                      <span className={`badge ${meta.className} flex-shrink-0 ms-2`}>{meta.labelKey ? t(meta.labelKey) : tx.status}</span>
                    </div>
                    <div className="text-muted small">
                      <i className="feather-briefcase me-1"></i>{tx.companyName} - {tx.customerName}
                    </div>
                    <div className="text-muted small">
                      <i className="feather-hash me-1"></i>{t('adminTransactionList.projectLabel')} {tx.projectNumber}
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                      <span className="small">{t('adminTransactionList.columns.projectValueSek')}: <strong>{currencyFormatter.format(tx.projectValueSek || 0)}</strong></span>
                      <span className="small">{t('adminTransactionList.total')}: <strong>{currencyFormatter.format(tx.totalPaidSek || 0)}</strong></span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="small text-success">{t('adminTransactionList.margin')}: {currencyFormatter.format(tx.etappioMarginSek || 0)}</span>
                      <span className="text-muted small">{formatDateTime(tx.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>
    </DashboardLayout>
  );
}
